import { PdfPage } from 'components/PdfExportConfigDialog/hooks/types';
import { Logger } from 'utilities';
import { capturePage } from './captureIframe';
import { PageResult } from './types';

// Static default when an explicit concurrency arg is invalid or no navigator
// signals are available; matches the resourceful tier so we don't regress on
// older runtimes that simply don't report capabilities.
export const DEFAULT_CONCURRENCY = 2;
export const MIN_CONCURRENCY = 1;
export const MAX_CONCURRENCY = 8;
// Default for a "resourceful" machine. Each parallel capture boots a full
// React app inside an iframe and runs html2canvas at 2x — two in flight is the
// sweet spot between throughput and request-quota burst (UserRateThrottle is
// 500/min and shared across all in-flight iframes). Weaker machines drop to
// MIN_CONCURRENCY so the export still completes without screen-blanking the
// host tab. The dialog slider still exposes up to MAX_CONCURRENCY for users
// who want to push past the default.
const RESOURCEFUL_DEFAULT_CONCURRENCY = 2;

// Thresholds below which we treat the machine as constrained and fall back to
// a single capture worker. Tuned to flag modest laptops/tablets while letting
// any reasonable developer or workstation hardware hit the parallel tier.
const RESOURCEFUL_MIN_CORES = 4;
const RESOURCEFUL_MIN_MEMORY_GB = 4;

// Optional capability signals we probe on `navigator`. `hardwareConcurrency`
// is widely supported; `deviceMemory` is Chromium-only; `cpuPerformance` is
// non-standard and treated as a best-effort performance hint when present.
interface NavigatorCapabilities {
  hardwareConcurrency?: number;
  deviceMemory?: number;
  cpuPerformance?: number;
}

const isResourcefulRuntime = (): boolean => {
  if (typeof navigator === 'undefined') {
    return true;
  }
  const nav = navigator as Navigator & NavigatorCapabilities;
  const cores = nav.hardwareConcurrency;
  const memoryGb = nav.deviceMemory;
  const perf = nav.cpuPerformance;

  if (typeof cores === 'number' && cores > 0 && cores < RESOURCEFUL_MIN_CORES) {
    return false;
  }
  if (typeof memoryGb === 'number' && memoryGb > 0 && memoryGb < RESOURCEFUL_MIN_MEMORY_GB) {
    return false;
  }
  if (typeof perf === 'number' && Number.isFinite(perf) && perf < 1) {
    return false;
  }
  return true;
};

/**
 * Default parallel-capture concurrency: `RESOURCEFUL_DEFAULT_CONCURRENCY` on a
 * machine that meets the CPU/memory/perf thresholds, otherwise
 * `MIN_CONCURRENCY`. The dialog slider can still be moved up to
 * `MAX_CONCURRENCY` from this default.
 */
export const getDefaultConcurrency = (): number =>
  isResourcefulRuntime() ? RESOURCEFUL_DEFAULT_CONCURRENCY : MIN_CONCURRENCY;

// Number of times the orchestrator will retry a single page if its iframe
// stalls (waitForReadiness exits with reason='stalled' because the React app
// inside the iframe is hung). Most stalls recover on a fresh iframe, where
// the second attempt gets new connections, a fresh React Query cache, etc.
const MAX_STALL_RETRIES = 1;

interface RunOrchestratorArgs {
  pages: PdfPage[];
  signal: AbortSignal;
  onPageUpdate: (result: PageResult) => void;
  concurrency?: number;
}

/**
 * Drives the capture pipeline with a configurable concurrency.
 * Order of returned results matches input order, regardless of completion order.
 */
export const runCapturePipeline = async ({
  pages,
  signal,
  onPageUpdate,
  concurrency,
}: RunOrchestratorArgs): Promise<PageResult[]> => {
  const results: PageResult[] = pages.map((p, index) => ({
    index,
    title: p.title,
    exportTarget: p.exportTarget,
    url: p.url,
    status: 'pending',
  }));

  let nextIndex = 0;

  const takeNext = (): number | null => {
    if (signal.aborted) {
      return null;
    }
    if (nextIndex >= pages.length) {
      return null;
    }
    const idx = nextIndex;
    nextIndex += 1;
    return idx;
  };

  const emit = (idx: number) => {
    onPageUpdate({ ...results[idx] });
  };

  const worker = async () => {
    while (!signal.aborted) {
      const idx = takeNext();
      if (idx === null) {
        return;
      }
      const page = pages[idx];

      results[idx] = { ...results[idx], status: 'loading', startedAt: performance.now() };
      emit(idx);

      // eslint-disable-next-line no-await-in-loop
      let verdict = await capturePage({ url: page.url, exportTarget: page.exportTarget, signal });
      // Retry once on a fresh iframe if the React app inside hung (stall
      // recovery). The teardown of the previous iframe in capturePage frees
      // the connections/cache that may have been the cause.
      let retries = 0;
      while (
        retries < MAX_STALL_RETRIES &&
        verdict.kind === 'failed' &&
        verdict.reason === 'stalled' &&
        !signal.aborted
      ) {
        retries += 1;
        Logger.warn(`Retrying stalled capture for ${page.exportTarget} (attempt ${retries + 1})`);
        // eslint-disable-next-line no-await-in-loop
        verdict = await capturePage({ url: page.url, exportTarget: page.exportTarget, signal });
      }

      if (signal.aborted || verdict.kind === 'canceled') {
        results[idx] = { ...results[idx], status: 'canceled', finishedAt: performance.now() };
        emit(idx);
        return;
      }

      if (verdict.kind === 'ready') {
        results[idx] = {
          ...results[idx],
          status: 'done',
          finishedAt: performance.now(),
          image: verdict.image,
          footer: verdict.footer,
        };
        emit(idx);
        // Release this worker's reference to the captured Blob now that the
        // consumer has its own copy via emit's spread. Holding it here would
        // keep every captured Blob alive in `results` until the entire
        // pipeline resolves, defeating the streaming PDF assembly downstream.
        results[idx] = { ...results[idx], image: null };
      } else if (verdict.kind === 'missing') {
        results[idx] = {
          ...results[idx],
          status: 'skipped',
          finishedAt: performance.now(),
          reason: 'target not present',
          image: null,
        };
        emit(idx);
      } else {
        results[idx] = {
          ...results[idx],
          status: 'failed',
          finishedAt: performance.now(),
          reason: verdict.reason,
          image: null,
        };
        emit(idx);
      }
    }
  };

  const resolvedConcurrency = Number.isFinite(concurrency) ? Number(concurrency) : getDefaultConcurrency();
  const clampedConcurrency = Math.max(
    MIN_CONCURRENCY,
    Math.min(MAX_CONCURRENCY, Math.floor(resolvedConcurrency) || DEFAULT_CONCURRENCY)
  );

  const workers: Promise<void>[] = [];
  const workerCount = Math.min(clampedConcurrency, pages.length);
  for (let i = 0; i < workerCount; i++) {
    workers.push(worker());
  }
  await Promise.all(workers);

  return results;
};
